import React, {
  useReducer,
  useState,
  useEffect,
  useRef,
  useContext
} from 'react'
import getConfig from 'next/config'
import { setCookie, parseCookies } from 'nookies'
import MarketingHelper from '@zuto-internal/zuto-marketing-helper'
import { GridItem } from '@zuto/core/src/grid'
import { PrivacyNotice, Loading } from '@zuto/molecules'
import $console from '@zuto/utilities/src/$console'
import { useCallbackOnce, useDidMountEffect } from '@zuto/utilities'
import ZutoAnalyticsHelper from '@zuto-internal/zuto-analytics-helper'
import { useDetectAdBlock } from 'adblock-detect-react'
import { ApplicationFormContext, Action, ApplicationFormState } from '../ApplicationFormContext'
import mapApplication, { Application } from '../../../Mappers/ApplicationFormMapper'
import SubmissionService from '../../../services/submission-service'
import EventsService from '../../../services/events-service'
import ErrorModal from '../../ErrorModal'
import { YEAR } from '../../../constants'
import { fireReactGAEvent } from '../../Questions/utils'
import { PrefillReviewPageApplicationFormContext } from '../PrefillReviewPageApplicationFormContext'
import { ApplicationFormProps } from '../types'
import Thankyou from './Thankyou'
import { zaSubmissionError } from '../../../utils/monitoring-utils/monitoring-utils'
import PrefillIntroductionV3 from '../../Introductions/PrefillIntroduction/PrefillIntroductionV3/PrefillIntroductionV3'
import RequiredDetailsFields from './RequiredDetailsFields'
import { sendFormAbandonmentEvent } from '../../../utils/monitoring-utils/sendFormAbandonmentEvent'
import { areEmployerDetailsRequired } from '../../Questions/CardlessEmploymentHistoryQuestion/EmploymentStatus/EmploymentStatusOptions'
import { mapRequiredDetails } from '../../../Mappers/RequiredDetailsMapper'


export const RequiredDetailsPageApplicationForm = ({
  vehicle,
  prefillData,
  lenderDetails,
  eventsServiceUrl,
  customerId,
  experimentName,
  isOfficeTraffic,
  isBotTraffic,
  journey,
  featureSwitches,
  loggingSwitches,
  attribution
}: ApplicationFormProps): JSX.Element => {
  const zutoAnalyticsHelper = new ZutoAnalyticsHelper()
  const { publicRuntimeConfig } = getConfig()
  const { cookieDomain } = publicRuntimeConfig
  const [loading, setLoading] = useState(false)
  const [apiError, setApiError] = useState(false)
  const [isDirty, setDirty] = useState(false)
  const [applicationFormCb, resetApplicationFormCb] = useCallbackOnce()
  const { Device: deviceId, Session: sessionId, zuto_wbraid: wbraid, zuto_gbraid: gbraid } = parseCookies()
  let adBlockDetected = true
  adBlockDetected = useDetectAdBlock()
  const { questionName, setExperimentName } = useContext(PrefillReviewPageApplicationFormContext)



  const requiredFields = {
    contactNumber: true,
    jobTitle: areEmployerDetailsRequired(prefillData?.employmentHistory?.entries['1'].employmentSituation)
  }

  useEffect(() => {
    setExperimentName(experimentName)
  }, [])

  const appFormEventParams = {
    Lender: lenderDetails?.lenderName,
    PreApproved: lenderDetails?.preapproved,
    QuoteId: lenderDetails?.quoteId,
    CustomerId: customerId,
    PartnerCustomerId: prefillData?.partnerCustomerId,
    Prefill: !!prefillData,
    DeviceId: deviceId,
    SessionId: sessionId,
    Experiments: experimentName,
    Journey: journey
  }

  const appFormMetadata = {
    deviceId: appFormEventParams.DeviceId,
    sessionId: appFormEventParams.SessionId,
    customerId: appFormEventParams.CustomerId,
    partnerCustomerId: appFormEventParams.PartnerCustomerId,
    lender: appFormEventParams.Lender,
    isPrefill: appFormEventParams.Prefill,
    isPreapproved: appFormEventParams.PreApproved,
    isBotTraffic,
    isOfficeTraffic,
    journey,
    experiments: experimentName,
    wbraid,
    gbraid
  }

  const eventsService = EventsService(eventsServiceUrl, appFormEventParams)

  const isRetry = useRef(false)

  const resetUnloadEvents = () => {
    global.onbeforeunload = null
    global.onunload = null
  }

  const submitApplication = (application: Application) => {
    setLoading(true)
    fireReactGAEvent({
      category: 'Application Form',
      action: 'Submit',
      label: 'FormSubmitted',
      lenderName: (prefillData) ? lenderDetails?.lenderName || 'zuto' : 'none'
    })
    SubmissionService(publicRuntimeConfig)
      .submitApplication(application)
      .then((response) => {
        try {
          fireReactGAEvent({
            category: 'Application Form',
            action: 'Submit',
            label: 'Success'
          })
        } catch {
          $console.error('Something went wrong with ReactGA services')
        }
        // fire-and-forget telemetry (don’t block redirect)
        void eventsService.sendAppFormSubmittedEvent(true, isRetry.current)
          .catch(err => $console.error('sendAppFormStartedEvent failed', err))
        resetUnloadEvents()
        window.location.assign(response.redirectUrl)
      })
      .catch((err) => {
        fireReactGAEvent({
          category: 'Application Form',
          action: 'Submit',
          label: 'Unsuccessful',
          lenderName: (prefillData) ? lenderDetails?.lenderName || 'zuto' : 'none'
        })
        // fire-and-forget telemetry 
        void eventsService.sendAppFormSubmittedEvent(false, isRetry.current)
          .catch(err => $console.error('sendAppFormStartedEvent failed', err))
        isRetry.current = true
        $console.error('Failed to submit application')
        zaSubmissionError(experimentName, err)
        setApiError(true)
      })
      .finally(() => {
        setLoading(false)
        setCookie(
          null,
          'emailAddress',
          application.applicantField[0].basicDetailsField.emailField,
          {
            maxAge: YEAR * 7,
            path: '/',
            domain: cookieDomain
          }
        )
        resetApplicationFormCb()
      })
  }

  const closeError = () => {
    setApiError(false)
    const submitButton = document.querySelector("button[name='submit-button']")
    if (submitButton) {
      (submitButton as HTMLElement).focus()
    }
  }

  const reducer = (
    state: ApplicationFormState,
    action: Action
  ): ApplicationFormState => {
    switch (action.type) {
      case 'SAVE':
        return {
          ...state,
          answers: {
            ...state.answers,
            [action.name]: action.value
          },
          allErrors: { ...state.allErrors, [action.name]: { ...action.errors } }
        }
      case 'SUBMIT': {
        const channelAttribution = MarketingHelper.getChannelAttribution()
        const mappedAnswers = mapRequiredDetails(state.answers, requiredFields)
        const result: Application = mapApplication(
          mappedAnswers,
          channelAttribution,
          experimentName,
          action.marketingPreferences,
          {
            ...appFormMetadata,
            isRetry: isRetry.current
          },
          vehicle,
          lenderDetails?.quoteId
        )
        applicationFormCb(() => submitApplication(result))
        return state
      }
      default:
        return state
    }
  }

  const initialState: ApplicationFormState = {
    currentQuestionIndex: 0,
    answers: prefillData || {},
    furthestQuestionIndex: 0,
    canProceed: true,
    postalCode: '',
    marketingPreferences: { email: false, sms: false },
    allErrors: {},
    prefillSection: ''
  }
  const [state, dispatch] = useReducer(reducer, initialState)

  const applicationFormIsDirty = () => {
    global.onbeforeunload = () => {
      sendFormAbandonmentEvent({
        eventType: 'beforeUnloadDialogRendered',
        questionName,
        deviceId,
        sessionId,
        experimentName
      })
      return true
    }

    // OnUnload may not be reliable with sending JS events.
    // We need a better way to detect for abandonment.
    global.onpagehide = () => {
      sendFormAbandonmentEvent({
        eventType: 'formAbandonment',
        questionName,
        deviceId,
        sessionId,
        experimentName
      })
      fireReactGAEvent({
        category: 'Application Form',
        action: 'Form Abandonment',
        label: questionName
      })
    }
  }

  useEffect(() => {
    // fire-and-forget telemetry 
    void eventsService.sendAppFormStartedEvent()
      .catch(err => $console.error('sendAppFormStartedEvent failed', err))
    if (adBlockDetected) {
      try {
        zutoAnalyticsHelper.sendCustomEvent({
          eventType: 'TrustPilotDidNotLoad'
        })
      } catch (e) {
        $console.error('zutoAnalyticsHelper.sendCustomEvent error', e)
      }
    }
  }, [eventsServiceUrl])

  useDidMountEffect(() => {
    if (!isDirty) {
      applicationFormIsDirty()
      setDirty(true)
    }
  }, [state.answers])



  return (
    <ApplicationFormContext.Provider
      value={{
        dispatch,
        getAppForm: () => state,
        featureSwitches,
        loggingSwitches
      }}
    >
      <div
        className="application-form-wrapper"
        data-experiment={experimentName}
      >
        <GridItem data-testid="app-introduction" gridColumn={['auto / span 12', '3 / span 8']} pb={4}>
          <PrefillIntroductionV3
            prefillData={state.answers}
            isReapply={false}
            isReviewPageV3PreApproval={true}
            lenderDetails={lenderDetails}
            isRequiredDetails
          />
          <RequiredDetailsFields requiredFields={requiredFields} questionKey="requiredDetails" />
        </GridItem>

        <Thankyou
          questionKey="thankYou"
          prefillData={state.answers}
          initialAnswerState={initialState.answers}
          attribution={attribution}
          requiredFields={requiredFields}
        />

        <GridItem
          gridColumn={['auto / span 12', '3 / span 8']}
          gridRow={['auto', '6 / auto']}
          mt={0}
        >
          <PrivacyNotice title="Your data is safe with Zuto" hideSecureIcon />
        </GridItem>
        {loading && <Loading />}
        {apiError && (
          <ErrorModal
            titleText="Sorry"
            ctaText="Please try again"
            onClick={closeError}
          >
            There was a problem submitting your application
          </ErrorModal>
        )}
      </div>

    </ApplicationFormContext.Provider>
  )
}
